import axiosInstance from "../../../middlewares/axiosInstance";
import { AxiosResponse } from "axios";
import {
  StockAdjustment,
  StockAdjustmentFormData,
  StockAdjustmentFilters,
  StockAdjustmentListResponse,
  ItemMaster,
  BatchInfo,
  Warehouse,
  Location,
  StockAdjustmentValidation,
} from "../types/stock-adjustment.types";

interface ListParams {
  searchQuery?: string;
  filters?: StockAdjustmentFilters;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  pageIndex?: number;
  pageSize?: number;
}

class StockAdjustmentService {
  // Stock Adjustments CRUD
  async listStockAdjustments(
    params: ListParams
  ): Promise<AxiosResponse<StockAdjustmentListResponse>> {
    return axiosInstance.post("/business-admin/stock-adjustments/list", {
      ...params,
    });
  }

  async getStockAdjustment(
    id: string
  ): Promise<AxiosResponse<{ data: StockAdjustment }>> {
    return axiosInstance.post("/business-admin/stock-adjustments/get", { id });
  }

  async createStockAdjustment(
    data: StockAdjustmentFormData
  ): Promise<AxiosResponse<{ data: StockAdjustment }>> {
    return axiosInstance.post("/business-admin/stock-adjustments/create", data);
  }

  async updateStockAdjustment(
    id: string,
    data: Partial<StockAdjustmentFormData>
  ): Promise<AxiosResponse<{ data: StockAdjustment }>> {
    return axiosInstance.post("/business-admin/stock-adjustments/update", {
      id,
      ...data,
    });
  }

  async deleteStockAdjustment(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/stock-adjustments/delete", {
      id,
    });
  }

  async approveStockAdjustment(
    id: string
  ): Promise<AxiosResponse<{ data: StockAdjustment }>> {
    return axiosInstance.post("/business-admin/stock-adjustments/approve", {
      id,
    });
  }

  async rejectStockAdjustment(
    id: string,
    reason: string
  ): Promise<AxiosResponse<{ data: StockAdjustment }>> {
    return axiosInstance.post("/business-admin/stock-adjustments/reject", {
      id,
      reason,
    });
  }

  // Validation
  async validateStockAdjustment(
    data: StockAdjustmentFormData
  ): Promise<AxiosResponse<StockAdjustmentValidation>> {
    return axiosInstance.post(
      "/business-admin/stock-adjustments/validate",
      data
    );
  }

  // Item Master (for item selector)
  async searchItemMaster(
    query: string,
    limit: number = 50
  ): Promise<AxiosResponse<{ data: ItemMaster[] }>> {
    return axiosInstance.post("/business-admin/item-master/search", {
      query,
      limit,
      include_stock_info: true,
    });
  }

  async getItemMaster(
    id: string
  ): Promise<AxiosResponse<{ data: ItemMaster }>> {
    return axiosInstance.post("/business-admin/item-master/get", { id });
  }

  // Batches (for batch-tracked items)
  async getItemBatches(
    item_id: string
  ): Promise<AxiosResponse<{ data: BatchInfo[] }>> {
    return axiosInstance.post("/business-admin/item-master/batches", {
      item_id,
      only_available: true,
    });
  }

  // Warehouses and Locations
  async listWarehouses(): Promise<
    AxiosResponse<{ data: { data: Warehouse[]; metadata: any } }>
  > {
    return axiosInstance.post("/business-admin/warehouses/list", {
      filters: [
        {
          id: "active_filter",
          title: "Active Status",
          field: "is_active",
          operator: "eq",
          value: true,
        },
      ],
      pageSize: 100,
    });
  }

  async getWarehouseLocations(
    warehouse_id: string
  ): Promise<AxiosResponse<{ data: { data: Location[]; metadata: any } }>> {
    return axiosInstance.post("/business-admin/locations/list", {
      filters: [
        {
          id: "warehouse_filter",
          title: "Warehouse",
          field: "warehouse_id",
          operator: "eq",
          value: warehouse_id,
        },
        {
          id: "active_filter",
          title: "Active Status",
          field: "is_active",
          operator: "eq",
          value: true,
        },
      ],
      pageSize: 100,
    });
  }

  // Bulk operations
  async bulkApproveStockAdjustments(
    ids: string[]
  ): Promise<AxiosResponse<{ success_count: number; failed_count: number }>> {
    return axiosInstance.post(
      "/business-admin/stock-adjustments/bulk-approve",
      { ids }
    );
  }

  async bulkRejectStockAdjustments(
    ids: string[],
    reason: string
  ): Promise<AxiosResponse<{ success_count: number; failed_count: number }>> {
    return axiosInstance.post("/business-admin/stock-adjustments/bulk-reject", {
      ids,
      reason,
    });
  }

  // Reports
  async getStockAdjustmentSummary(filters: StockAdjustmentFilters): Promise<
    AxiosResponse<{
      total_adjustments: number;
      total_quantity_adjusted: number;
      adjustments_by_type: Record<string, number>;
      adjustments_by_status: Record<string, number>;
    }>
  > {
    return axiosInstance.post("/business-admin/stock-adjustments/summary", {
      filters,
    });
  }

  // Export
  async exportStockAdjustments(
    filters: StockAdjustmentFilters,
    format: "excel" | "csv" = "excel"
  ): Promise<AxiosResponse<{ download_url: string }>> {
    return axiosInstance.post("/business-admin/stock-adjustments/export", {
      filters,
      format,
    });
  }
}

export const stockAdjustmentService = new StockAdjustmentService();
